// src/components/StaffManagement/StaffManagement.js
import React, { useState, useEffect } from "react";
import "./StaffManagement.css";
import Sidebar from "../Sidebar/Sidebar";
import { cloudApi } from "../../api";
import { useNavigate } from "react-router-dom";

const extractErrorMessage = (err) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => (typeof e === "string" ? e : e.msg || "Validation error")).join("; ");
    if (typeof detail === "object" && detail.msg) return detail.msg;
    return String(detail);
  }
  return err?.message || "An error occurred";
};

const StaffManagement = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const [activeView, setActiveView] = useState("list");
  const [staffList, setStaffList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);

  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [staffForPermission, setStaffForPermission] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [permissionLoading, setPermissionLoading] = useState(false);

  const [searchName, setSearchName] = useState("");
  const [searchRole, setSearchRole] = useState("");
  const [searchBranch, setSearchBranch] = useState("");

  const [formData, setFormData] = useState({
    staff_name: "", staff_email: "", staff_phone: "",
    user_name: "", password: "", staff_role: "cashier", branch_id: null,
  });
  const [creationPermissions, setCreationPermissions] = useState([]);
  const [editData, setEditData] = useState({ staff_role: "", is_active: true });

  // ── Phone number change state ──
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [staffForPhoneChange, setStaffForPhoneChange] = useState(null);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [phoneChangeLoading, setPhoneChangeLoading] = useState(false);

  // ── React modal states ──
  const [confirmDialog, setConfirmDialog] = useState({
    visible: false, title: "", message: "", onConfirm: null,
  });
  const [alertDialog, setAlertDialog] = useState({
    visible: false, title: "", message: "", onClose: null,
  });

  // ── React confirm helper ──
  const showConfirm = (title, message) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        visible: true, title, message,
        onConfirm: (result) => {
          setConfirmDialog({ visible: false, title: "", message: "", onConfirm: null });
          resolve(result);
        },
      });
    });
  };

  // ── React alert helper ──
  const showAlert = (title, message) => {
    return new Promise((resolve) => {
      setAlertDialog({
        visible: true, title, message,
        onClose: () => {
          setAlertDialog({ visible: false, title: "", message: "", onClose: null });
          resolve();
        },
      });
    });
  };

  const staffRoles = [
    { value: "manager", label: "Manager", description: "Almost full access except critical settings" },
    { value: "pharmacist", label: "Pharmacist", description: "POS, prescriptions, inventory view" },
    { value: "cashier", label: "Cashier", description: "POS and basic sales only" },
    { value: "inventory_manager", label: "Inventory Manager", description: "Stock management" },
    { value: "sales_clerk", label: "Sales Clerk", description: "Limited POS access" },
  ];

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) { navigate("/Login"); return; }
    if (!navigator.onLine) { setIsLoading(false); return; }
    const initialize = async () => {
      try {
        await Promise.all([loadCurrentUser(), fetchBranches(), fetchAvailablePermissions(), fetchStaffList()]);
      } catch (err) {
        if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
          setIsOnline(false);
        } else {
          setError(extractErrorMessage(err));
        }
      } finally {
        setIsLoading(false);
      }
    };
    initialize();
  }, [navigate]);

  useEffect(() => {
    if (activeView === "list" && !isLoading && isOnline) fetchStaffList();
  }, [activeView, isLoading, isOnline]);

  const loadCurrentUser = async () => {
    const res = await cloudApi.get("/auth/me");
    setCurrentUser(res.data);
  };

  const fetchBranches = async () => {
    try {
      const res = await cloudApi.get("/users/pharmacies/branches");
      setBranches(res.data.branches || []);
    } catch {
      // non-fatal — branch selector will just be empty
    }
  };

  const fetchAvailablePermissions = async () => {
    try {
      const res = await cloudApi.get("/staffmanager/my-permissions");
      const perms = res.data || [];
      // ── Ensure "discount" is always present in the assignable list ──
      if (!perms.includes("discount")) {
        perms.push("discount");
      }
      setAvailablePermissions(perms);
    } catch {
      // non-fatal — permission list will just be empty
    }
  };

  const fetchStaffList = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await cloudApi.get("/staffmanager/list");
      setStaffList(res.data || []);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true); setError("");
      const params = {};
      if (searchName) params.name = searchName;
      if (searchRole) params.role = searchRole;
      if (searchBranch) params.branch_id = searchBranch;
      const res = await cloudApi.get("/staffmanager/staff/search/all", { params });
      setStaffList(res.data || []);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => { setSearchName(""); setSearchRole(""); setSearchBranch(""); fetchStaffList(); };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    if (!formData.staff_name || !formData.staff_email || !formData.staff_phone || !formData.user_name || !formData.password) { setError("All fields are required"); return; }
    if (formData.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    try {
      setLoading(true); setError(""); setSuccess("");
      const payload = { ...formData, branch_id: formData.branch_id === "" || formData.branch_id === "0" ? null : formData.branch_id };
      const createRes = await cloudApi.post("/staffmanager/create", payload);
      const newStaffId = createRes.data.id || createRes.data.staff_id;
      if (creationPermissions.length > 0 && newStaffId) {
        for (const permission of creationPermissions) {
          try { await cloudApi.put(`/staffmanager/${newStaffId}/permissions`, { action: "add", permission }); } catch { }
        }
      }
      setSuccess(`Staff member ${formData.staff_name} created successfully!`);
      setFormData({ staff_name: "", staff_email: "", staff_phone: "", user_name: "", password: "", staff_role: "cashier", branch_id: null });
      setCreationPermissions([]);
      setTimeout(() => { setActiveView("list"); setSuccess(""); }, 2000);
    } catch (err) { setError(extractErrorMessage(err)); }
    finally { setLoading(false); }
  };

  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    if (!selectedStaff) return;
    try {
      setLoading(true); setError(""); setSuccess("");
      await cloudApi.put(`/staffmanager/${selectedStaff.id}`, editData);
      setSuccess("Staff member updated successfully!");
      setTimeout(() => { setActiveView("list"); setSelectedStaff(null); setSuccess(""); }, 2000);
    } catch (err) { setError(extractErrorMessage(err)); }
    finally { setLoading(false); }
  };

  const handleDeleteStaff = async (staffId, staffName) => {
    const confirmed = await showConfirm(
      "🗑️ Delete Staff Member",
      `Are you sure you want to delete ${staffName}? This will revoke their access immediately.`
    );
    if (!confirmed) return;
    try {
      setLoading(true); setError("");
      await cloudApi.delete(`/staffmanager/${staffId}`);
      setSuccess("Staff member deleted successfully");
      fetchStaffList();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(extractErrorMessage(err)); }
    finally { setLoading(false); }
  };

  const handleOpenPermissionModal = (staff) => {
    setStaffForPermission(staff);
    setSelectedPermissions(staff.permissions || []);
    setOriginalPermissions(staff.permissions || []);
    setShowPermissionModal(true);
  };

  const handlePermissionChange = (permission) => setSelectedPermissions(prev => prev.includes(permission) ? prev.filter(p => p !== permission) : [...prev, permission]);
  const handleCreationPermissionChange = (permission) => setCreationPermissions(prev => prev.includes(permission) ? prev.filter(p => p !== permission) : [...prev, permission]);

  const handleAssignPermissions = async () => {
    if (!staffForPermission) return;
    try {
      setPermissionLoading(true); setError("");
      const permissionsToAdd = selectedPermissions.filter(p => !originalPermissions.includes(p));
      const permissionsToRemove = originalPermissions.filter(p => !selectedPermissions.includes(p));
      for (const permission of permissionsToAdd) await cloudApi.put(`/staffmanager/${staffForPermission.id}/permissions`, { action: "add", permission });
      for (const permission of permissionsToRemove) await cloudApi.put(`/staffmanager/${staffForPermission.id}/permissions`, { action: "remove", permission });
      setSuccess(`Permissions updated for ${staffForPermission.staff_name}!`);
      setShowPermissionModal(false); setStaffForPermission(null); setSelectedPermissions([]); setOriginalPermissions([]);
      setTimeout(() => { fetchStaffList(); setSuccess(""); }, 1500);
    } catch (err) { setError(extractErrorMessage(err)); }
    finally { setPermissionLoading(false); }
  };

  // ── Phone number change handlers ──
  const handleOpenPhoneModal = (staff) => {
    setStaffForPhoneChange(staff);
    setNewPhoneNumber("");
    setError("");
    setShowPhoneModal(true);
  };

  const handleClosePhoneModal = () => {
    setShowPhoneModal(false);
    setStaffForPhoneChange(null);
    setNewPhoneNumber("");
    setError("");
  };

  const handleStaffPhoneChange = async () => {
    if (!staffForPhoneChange) return;

    if (!newPhoneNumber.trim()) { setError("Please enter a phone number."); return; }
    if (!/^\d+$/.test(newPhoneNumber)) { setError("Phone number must contain only digits."); return; }
    if (newPhoneNumber.length < 10 || newPhoneNumber.length > 15) { setError("Phone number must be between 10 and 15 digits."); return; }
    if (newPhoneNumber === staffForPhoneChange.staff_phone) { setError("New phone number is the same as the current one."); return; }

    try {
      setPhoneChangeLoading(true);
      setError("");
      const formDataUpload = new FormData();
      formDataUpload.append("new_phone_number", newPhoneNumber);
      await cloudApi.put(
        `/users/pharmacy/staff/${staffForPhoneChange.id}/phone-number`,
        formDataUpload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSuccess(`Phone number updated for ${staffForPhoneChange.staff_name}.`);
      handleClosePhoneModal();
      fetchStaffList();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setPhoneChangeLoading(false);
    }
  };

  const handleEditClick = (staff) => { setSelectedStaff(staff); setEditData({ staff_role: staff.staff_role, is_active: staff.is_active }); setActiveView("edit"); };
  const handleInputChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };
  const handleEditInputChange = (e) => { const { name, value, type, checked } = e.target; setEditData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value })); };

  const getRoleBadgeClass = (role) => ({ manager: "role-manager", pharmacist: "role-pharmacist", cashier: "role-cashier", inventory_manager: "role-inventory", sales_clerk: "role-sales" })[role] || "role-default";

  const getLocationDisplay = (staff) => {
    if (staff.branch?.name) return { name: staff.branch.name, type: "branch" };
    if (staff.branch_id) { const branch = branches.find(b => b.id === staff.branch_id); if (branch) return { name: branch.branch_name, type: "branch" }; }
    return { name: "Main Branch", type: "main_pharmacy" };
  };

  // ── No internet screen ──
  if (!isOnline) {
    return (
      <div className="staff-layout">
        {!isMobile && <Sidebar />}
        <div className="staff-container" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", textAlign: "center" }}>
          <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>📡</div>
          <h2 style={{ color: "#333", marginBottom: "0.5rem" }}>No Internet Connection</h2>
          <p style={{ color: "#666", maxWidth: "400px", lineHeight: "1.6", marginBottom: "1.5rem" }}>
            Staff management requires an internet connection. Please check your network and try again.
          </p>
          <button
            onClick={() => {
              if (navigator.onLine) { setIsOnline(true); setIsLoading(true); window.location.reload(); }
              else setError("Still no internet connection. Please check your network settings.");
            }}
            style={{ padding: "0.75rem 2rem", background: "#007bff", color: "white", border: "none", borderRadius: "6px", fontSize: "1rem", cursor: "pointer", fontWeight: "500" }}
          >
            🔄 Retry
          </button>
          {error && <p style={{ color: "#dc3545", marginTop: "1rem", fontSize: "0.9rem" }}>{error}</p>}
        </div>
      </div>
    );
  }

  // ── Loading screen ──
  if (isLoading) {
    return (
      <div className="staff-layout">
        {!isMobile && <Sidebar />}
        <div className="staff-container" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
          <div style={{ width: "40px", height: "40px", border: "4px solid #f3f3f3", borderTop: "4px solid #007bff", borderRadius: "50%", animation: "spin 1s linear infinite", marginBottom: "1rem" }} />
          <p style={{ color: "#666", fontSize: "1rem" }}>Loading staff management...</p>
          <p style={{ color: "#999", fontSize: "0.85rem", marginTop: "0.5rem" }}>Connecting to cloud server...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-layout">
      {!isMobile && <Sidebar />}
      <div className="staff-container">

        {/* ── React Alert Modal ── */}
        {alertDialog.visible && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <div style={{ background: "#fff", borderRadius: "10px", padding: "2rem", maxWidth: "460px", width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
              <h3 style={{ margin: "0 0 1rem", color: "#333", fontSize: "1.1rem" }}>{alertDialog.title}</h3>
              <p style={{ color: "#555", fontSize: "0.95rem", lineHeight: 1.6, margin: "0 0 1.5rem", whiteSpace: "pre-line" }}>{alertDialog.message}</p>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={alertDialog.onClose}
                  style={{ padding: "0.6rem 1.5rem", background: "#007bff", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── React Confirm Modal ── */}
        {confirmDialog.visible && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <div style={{ background: "#fff", borderRadius: "10px", padding: "2rem", maxWidth: "460px", width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
              <h3 style={{ margin: "0 0 1rem", color: "#333", fontSize: "1.1rem" }}>{confirmDialog.title}</h3>
              <p style={{ color: "#555", fontSize: "0.95rem", lineHeight: 1.6, margin: "0 0 1.5rem", whiteSpace: "pre-line" }}>{confirmDialog.message}</p>
              <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
                <button
                  onClick={() => confirmDialog.onConfirm(false)}
                  style={{ padding: "0.6rem 1.25rem", background: "#f8f9fa", color: "#333", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontWeight: 500, fontSize: "0.95rem" }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmDialog.onConfirm(true)}
                  style={{ padding: "0.6rem 1.25rem", background: "#dc3545", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="staff-header-section">
          <h2>👥 Staff Management</h2>
          {currentUser && (
            <div className="user-info-badge">
              <span className="user-name">{currentUser.name}</span>
              <span className="user-role">{currentUser.user_type}</span>
            </div>
          )}
        </div>

        {!isOnline && (
          <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: "8px", padding: "1rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <span style={{ fontSize: "1.5rem" }}>📡</span>
            <div>
              <strong style={{ color: "#856404" }}>No internet connection</strong>
              <p style={{ margin: 0, color: "#856404", fontSize: "0.9rem" }}>Staff management actions require internet. Please reconnect to continue.</p>
            </div>
          </div>
        )}

        <div className="staff-nav-menu">
          <button className={activeView === "list" ? "nav-btn active" : "nav-btn"} onClick={() => setActiveView("list")}>📋 Staff List</button>
          <button className={activeView === "create" ? "nav-btn active" : "nav-btn"} onClick={() => setActiveView("create")}>➕ Add New Staff</button>
        </div>

        {/* ===== STAFF LIST VIEW ===== */}
        {activeView === "list" && (
          <div className="staff-list-view">
            <div className="search-bar">
              <input type="text" placeholder="Search by name..." value={searchName} onChange={(e) => setSearchName(e.target.value)} className="search-input" />
              <select value={searchRole} onChange={(e) => setSearchRole(e.target.value)} className="search-select">
                <option value="">All Roles</option>
                {staffRoles.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
              </select>
              <select value={searchBranch} onChange={(e) => setSearchBranch(e.target.value)} className="search-select">
                <option value="">All Branches</option>
                {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.branch_name}</option>)}
              </select>
              <button className="search-btn" onClick={handleSearch}>🔍 Search</button>
              <button className="clear-btn" onClick={clearSearch}>✖ Clear</button>
            </div>

            {loading && <p className="loading-text">Loading staff...</p>}

            {!loading && staffList.length === 0 && (
              <div className="empty-state">
                <p>No staff members found.</p>
                <button className="add-first-btn" onClick={() => setActiveView("create")}>➕ Add Your First Staff Member</button>
              </div>
            )}

            <div className="staff-grid">
              {staffList.map((staff) => {
                const location = getLocationDisplay(staff);
                return (
                  <div key={staff.id} className="staff-card">
                    <div className="staff-header">
                      <div className="staff-avatar">{staff.staff_name.charAt(0).toUpperCase()}</div>
                      <div className="staff-info">
                        <h3>{staff.staff_name}</h3>
                        <span className={`role-badge ${getRoleBadgeClass(staff.staff_role)}`}>{staff.staff_role.replace("_", " ").toUpperCase()}</span>
                      </div>
                    </div>
                    <div className="staff-details">
                      <p><strong>📧 Email:</strong> {staff.staff_email}</p>
                      <p><strong>📱 Phone:</strong> {staff.staff_phone}</p>
                      <p><strong>🏢 Location:</strong> <span className={`location-badge ${location.type === "branch" ? "location-branch" : "location-main"}`}>{location.name}</span></p>
                      <p><strong>📅 Created:</strong> {new Date(staff.created_at).toLocaleDateString()}</p>
                      <p><strong>Status:</strong> <span className={staff.is_active ? "status-active" : "status-inactive"}>{staff.is_active ? " ✓ Active" : " ✗ Inactive"}</span></p>
                    </div>
                    <div className="staff-permissions">
                      <strong>🔑 Permissions ({staff.permissions?.length || 0}):</strong>
                      <div className="permissions-list">
                        {staff.permissions?.slice(0, 3).map((perm, idx) => <span key={idx} className="permission-tag">{typeof perm === "string" ? perm.replace("_", " ") : "Permission"}</span>)}
                        {staff.permissions?.length > 3 && <span className="permission-tag more">+{staff.permissions.length - 3} more</span>}
                      </div>
                    </div>
                    <div className="staff-actions">
                      <button className="view-btn" onClick={() => navigate(`/PharmacyStaffprofile/${staff.id}`)}>👁️ View Profile</button>
                      <button className="permission-btn" onClick={() => handleOpenPermissionModal(staff)}>🔐 Permissions</button>
                      <button className="edit-btn" onClick={() => handleEditClick(staff)}>✏️ Edit</button>
                      <button
                        className="edit-btn"
                        style={{ background: "#17a2b8", color: "white" }}
                        onClick={() => handleOpenPhoneModal(staff)}
                      >
                        📱 Change Phone
                      </button>
                      <button className="delete-btn" onClick={() => handleDeleteStaff(staff.id, staff.staff_name)}>🗑️ Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== CREATE STAFF VIEW ===== */}
        {activeView === "create" && (
          <div className="staff-form-view">
            <h3>➕ Add New Staff Member</h3>
            <form onSubmit={handleCreateStaff} className="staff-form">
              <div className="form-group"><label>Staff Name *</label><input type="text" name="staff_name" value={formData.staff_name} onChange={handleInputChange} placeholder="Enter full name" required /></div>
              <div className="form-group"><label>Email Address *</label><input type="email" name="staff_email" value={formData.staff_email} onChange={handleInputChange} placeholder="staff@example.com" required /></div>
              <div className="form-group"><label>Phone Number *</label><input type="tel" name="staff_phone" value={formData.staff_phone} onChange={handleInputChange} placeholder="08012345678" required /></div>
              <div className="form-group"><label>Username *</label><input type="text" name="user_name" value={formData.user_name} onChange={handleInputChange} placeholder="username (no spaces)" required /></div>
              <div className="form-group"><label>Password *</label><input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder="Minimum 6 characters" required /></div>
              <div className="form-group">
                <label>Staff Role *</label>
                <select name="staff_role" value={formData.staff_role} onChange={handleInputChange} required>
                  {staffRoles.map((role) => <option key={role.value} value={role.value}>{role.label} - {role.description}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Branch (Optional)</label>
                <select name="branch_id" value={formData.branch_id || ""} onChange={handleInputChange}>
                  <option value="">Main Branch</option>
                  {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.branch_name}</option>)}
                </select>
                <small className="form-hint">Leave as "Main Branch" if this staff works at the main location</small>
              </div>
              <div className="form-group permissions-group">
                <label>🔐 Assign Permissions (Optional)</label>
                <p className="permission-hint">Select permissions for this staff member</p>
                <div className="creation-permissions-grid">
                  {availablePermissions.map((permission) => (
                    <div key={permission} className="permission-checkbox-inline">
                      <input type="checkbox" id={`create-perm-${permission}`} checked={creationPermissions.includes(permission)} onChange={() => handleCreationPermissionChange(permission)} />
                      <label htmlFor={`create-perm-${permission}`}>{permission.replace(/_/g, " ")}</label>
                    </div>
                  ))}
                </div>
                {creationPermissions.length > 0 && (
                  <div className="selected-creation-permissions">
                    <p className="selection-count"><strong>{creationPermissions.length}</strong> permission(s) selected</p>
                    <div className="selected-creation-list">{creationPermissions.map((perm) => <span key={perm} className="selected-creation-tag">{perm.replace(/_/g, " ")}</span>)}</div>
                  </div>
                )}
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={loading}>{loading ? "Creating..." : "✓ Create Staff"}</button>
                <button type="button" className="cancel-btn" onClick={() => { setActiveView("list"); setCreationPermissions([]); }}>✖ Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* ===== EDIT STAFF VIEW ===== */}
        {activeView === "edit" && selectedStaff && (
          <div className="staff-form-view">
            <h3>✏️ Edit Staff Member</h3>
            <div className="edit-info">
              <p><strong>Name:</strong> {selectedStaff.staff_name}</p>
              <p><strong>Email:</strong> {selectedStaff.staff_email}</p>
              <p><strong>Phone:</strong> {selectedStaff.staff_phone}</p>
            </div>
            <form onSubmit={handleUpdateStaff} className="staff-form">
              <div className="form-group">
                <label>Staff Role *</label>
                <select name="staff_role" value={editData.staff_role} onChange={handleEditInputChange} required>
                  {staffRoles.map((role) => <option key={role.value} value={role.value}>{role.label} - {role.description}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Account Status</label>
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: "1rem",
                    padding: "0.85rem 1rem", borderRadius: "8px",
                    border: `1px solid ${editData.is_active ? "#28a745" : "#dc3545"}`,
                    background: editData.is_active ? "#f0fff4" : "#fff5f5",
                    cursor: "pointer", userSelect: "none", transition: "all 0.2s ease",
                  }}
                  onClick={() => setEditData(prev => ({ ...prev, is_active: !prev.is_active }))}
                >
                  <div style={{
                    position: "relative", width: "48px", height: "26px",
                    borderRadius: "13px", background: editData.is_active ? "#28a745" : "#ccc",
                    transition: "background 0.25s ease", flexShrink: 0,
                  }}>
                    <div style={{
                      position: "absolute", top: "3px",
                      left: editData.is_active ? "25px" : "3px",
                      width: "20px", height: "20px", borderRadius: "50%",
                      background: "white", boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                      transition: "left 0.25s ease",
                    }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: "600", fontSize: "0.95rem", color: editData.is_active ? "#28a745" : "#dc3545" }}>
                      {editData.is_active ? "✓ Active" : "✗ Inactive"}
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#666", marginTop: "1px" }}>
                      {editData.is_active
                        ? "Staff can log in and perform their duties"
                        : "Staff access is suspended — all sessions will be revoked"}
                    </div>
                  </div>
                </div>
                {!editData.is_active && (
                  <div style={{
                    marginTop: "0.6rem", padding: "0.65rem 0.9rem",
                    background: "#fff3cd", border: "1px solid #ffc107",
                    borderRadius: "6px", fontSize: "0.85rem", color: "#856404",
                  }}>
                    <strong>⚠️ Warning:</strong> Deactivating will revoke all refresh tokens and force an immediate logout.
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={loading}>{loading ? "Updating..." : "✓ Update Staff"}</button>
                <button type="button" className="cancel-btn" onClick={() => { setActiveView("list"); setSelectedStaff(null); }}>✖ Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* ===== ERROR / SUCCESS MESSAGES ===== */}
        {error && typeof error === "string" && <div className="error-message">{error}</div>}
        {success && typeof success === "string" && <div className="success-message">{success}</div>}

        {/* ===== PERMISSION MODAL ===== */}
        {showPermissionModal && staffForPermission && (
          <div className="modal-overlay" onClick={() => setShowPermissionModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>🔐 Assign Permissions</h3>
                <button className="modal-close" onClick={() => setShowPermissionModal(false)}>✕</button>
              </div>
              <div className="modal-body">
                <p className="staff-name">Staff: <strong>{staffForPermission.staff_name}</strong></p>
                <p className="staff-role">Role: <strong>{staffForPermission.staff_role.replace("_", " ").toUpperCase()}</strong></p>
                <div className="permissions-section">
                  <h4>Available Permissions:</h4>
                  <div className="permissions-checkboxes">
                    {availablePermissions.map((permission) => (
                      <div key={permission} className="permission-checkbox">
                        <input type="checkbox" id={`perm-${permission}`} checked={selectedPermissions.includes(permission)} onChange={() => handlePermissionChange(permission)} />
                        <label htmlFor={`perm-${permission}`}>{permission.replace(/_/g, " ")}</label>
                      </div>
                    ))}
                  </div>
                </div>
                {selectedPermissions.length > 0 && (
                  <div className="selected-permissions">
                    <h4>Selected Permissions ({selectedPermissions.length}):</h4>
                    <div className="selected-list">
                      {selectedPermissions.map((perm) => (
                        <span key={perm} className="selected-tag">
                          {perm.replace(/_/g, " ")}
                          <button type="button" onClick={() => handlePermissionChange(perm)} className="remove-btn">✕</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="modal-cancel-btn" onClick={() => setShowPermissionModal(false)}>Cancel</button>
                <button className="modal-submit-btn" onClick={handleAssignPermissions} disabled={permissionLoading}>{permissionLoading ? "Saving..." : "✓ Save Permissions"}</button>
              </div>
            </div>
          </div>
        )}

        {/* ===== PHONE NUMBER CHANGE MODAL ===== */}
        {showPhoneModal && staffForPhoneChange && (
          <div className="modal-overlay" onClick={handleClosePhoneModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>📱 Change Phone Number</h3>
                <button className="modal-close" onClick={handleClosePhoneModal}>✕</button>
              </div>
              <div className="modal-body">
                <p className="staff-name">
                  Staff: <strong>{staffForPhoneChange.staff_name}</strong>
                </p>
                <p style={{ color: "#666", fontSize: "0.9rem", marginBottom: "1.2rem" }}>
                  Current phone: <strong>{staffForPhoneChange.staff_phone}</strong>
                </p>
                <div className="form-group">
                  <label style={{ fontWeight: 600, marginBottom: "0.4rem", display: "block" }}>
                    New Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={newPhoneNumber}
                    onChange={(e) => { setNewPhoneNumber(e.target.value); setError(""); }}
                    placeholder="e.g. 08012345678"
                    style={{
                      width: "100%", padding: "0.75rem",
                      border: "1px solid #ddd", borderRadius: "6px",
                      fontSize: "1rem", boxSizing: "border-box",
                    }}
                    disabled={phoneChangeLoading}
                    autoFocus
                  />
                  <small style={{ color: "#888", fontSize: "0.8rem", marginTop: "0.3rem", display: "block" }}>
                    Must be 10–15 digits, numbers only.
                  </small>
                </div>
                {error && (
                  <div style={{
                    background: "#fff5f5", border: "1px solid #dc3545",
                    borderRadius: "6px", padding: "0.65rem 0.9rem",
                    color: "#dc3545", fontSize: "0.875rem", marginTop: "0.75rem",
                  }}>
                    {error}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="modal-cancel-btn" onClick={handleClosePhoneModal} disabled={phoneChangeLoading}>Cancel</button>
                <button
                  className="modal-submit-btn"
                  onClick={handleStaffPhoneChange}
                  disabled={phoneChangeLoading || !newPhoneNumber.trim()}
                >
                  {phoneChangeLoading ? "Updating..." : "✓ Update Phone"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default StaffManagement;